import React from "react";
import { Link } from "react-router-dom";
import "./Term.css";

import Header from "./Header";
import Footer from "./Footer";

const LAST_UPDATED = "August 7, 2026";

const sections = [
  { id: "overview", title: "1. Overview" },
  { id: "information-we-collect", title: "2. Information We Collect" },
  { id: "how-we-use", title: "3. How We Use Your Information" },
  { id: "phone-numbers", title: "4. A Note on Phone Numbers" },
  { id: "payments", title: "5. Payments (HubMart)" },
  { id: "business-listings", title: "6. Business Listing Information" },
  { id: "sharing", title: "7. How We Share Information" },
  { id: "service-providers", title: "8. Service Providers We Use" },
  { id: "retention", title: "9. Data Retention" },
  { id: "security", title: "10. Security" },
  { id: "your-rights", title: "11. Your Choices & Rights" },
  { id: "children", title: "12. Children's Privacy" },
  { id: "mobile-app", title: "13. Mobile App" },
  { id: "changes", title: "14. Changes to This Policy" },
  { id: "contact", title: "15. Contact Us" },
];

function PrivacyPage() {
  return (
    <>
      <Header />

      <div className="terms-page" id="privacy">
        {/* ── Page header ── */}
        <p className="terms-eyebrow">Legal</p>
        <h2 className="terms-title">Privacy Policy</h2>
        <p className="terms-updated">Last updated: {LAST_UPDATED}</p>
        <p className="terms-intro">
          At <span className="brand">GoOurHub</span>, we connect communities
          and local businesses and we believe doing that well starts with
          respecting your privacy. This Privacy Policy explains what
          information we collect, why we collect it, and the choices you have.
          It applies to the GoOurHub website, our mobile app, the HubMart
          marketplace, and related services (collectively, the "Platform").
          This policy works together with our{" "}
          <Link to="/terms" className="terms-link">Terms &amp; Conditions</Link>.
        </p>

        {/* ── Table of contents ── */}
        <nav className="terms-toc" aria-label="Table of contents">
          <p className="toc-heading">On this page</p>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.title}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* ── 1. Overview ── */}
        <section id="overview" className="terms-section">
          <h3>1. Overview</h3>
          <p>
            We collect only the information we need to run the Platform, and we
            keep it simple: we do not sell your personal information, we do not
            use it for third-party advertising, and we do not track you across
            other apps or websites.
          </p>
        </section>

        {/* ── 2. What we collect ── */}
        <section id="information-we-collect" className="terms-section">
          <h3>2. Information We Collect</h3>
          <p>
            <strong>Information you give us directly:</strong>
          </p>
          <ul>
            <li>
              <strong>Name</strong> (first and last name) when you create an
              account, sign up as a partner, or contact us.
            </li>
            <li>
              <strong>Email address</strong> when you create an account,
              verify your identity, list a business, or reach out to us.
            </li>
            <li>
              <strong>Phone number</strong> collected during signup (see
              Section 4 below for how it is and isn't used).
            </li>
            <li>
              <strong>Business information</strong> if you list a business:
              business name, category, community, address, description, images,
              offers, and contact details you choose to display publicly.
            </li>
            <li>
              <strong>Messages</strong> anything you send us through contact
              or request forms.
            </li>
          </ul>
          <p>
            <strong>Information collected automatically:</strong> like most
            online services, our hosting and infrastructure providers keep
            standard technical logs (such as IP address, browser or device
            type, and pages visited) for security and reliability purposes.
          </p>
        </section>

        {/* ── 3. How we use ── */}
        <section id="how-we-use" className="terms-section">
          <h3>3. How We Use Your Information</h3>
          <ul>
            <li>
              <strong>Email verification:</strong> we send a verification code
              to your email to confirm it's really you when you sign up or sign
              in.
            </li>
            <li>
              <strong>Account &amp; listing management:</strong> to create and
              maintain your account, publish and manage business listings, and
              respond to claim, edit, or removal requests.
            </li>
            <li>
              <strong>Communication:</strong> to reply when you contact us and
              to send important service notices about your account or listing.
            </li>
            <li>
              <strong>Platform safety &amp; improvement:</strong> to review
              listings for accuracy and trustworthiness, prevent fraud and
              abuse, and keep the Platform running smoothly.
            </li>
          </ul>
          <p>
            We do not use your information for automated decision-making or
            profiling, and we do not send marketing emails without your
            consent.
          </p>
        </section>

        {/* ── 4. Phone numbers ── */}
        <section id="phone-numbers" className="terms-section">
          <h3>4. A Note on Phone Numbers</h3>
          <p>
            We ask for a phone number at signup, but we do not currently use it
            for anything, no calls, no texts, no verification, no sharing. It
            is simply stored with your account. If we ever begin using phone
            numbers (for example, for SMS verification), we will update this
            policy first and let you know. If you'd like your phone number
            deleted from our records, just ask (see Section 11).
          </p>
        </section>

        {/* ── 5. Payments ── */}
        <section id="payments" className="terms-section">
          <h3>5. Payments (HubMart)</h3>
          <p>
            Purchases on HubMart are processed by{" "}
            <strong>Stripe</strong>, a third-party payment processor.{" "}
            <strong>
              GoOurHub never sees or stores your full card number.
            </strong>{" "}
            Payment details go directly to Stripe, which handles them under its
            own privacy policy (available at stripe.com/privacy). We may
            receive limited transaction information (such as the amount and
            confirmation of payment) needed to complete your order.
          </p>
        </section>

        {/* ── 6. Business listings ── */}
        <section id="business-listings" className="terms-section">
          <h3>6. Business Listing Information</h3>
          <p>
            GoOurHub displays business listings compiled from publicly
            available sources, directories, and community submissions. This
            information (business names, addresses, categories, and public
            contact details) is business information, not private personal
            data. If a listing relates to you and you would like to claim,
            correct, or remove it, please use our{" "}
            <Link to="/partner-signup#joinForm" className="terms-link">
              <strong>Claim / Opt-Out / Suggest Edits form</strong>
            </Link>{" "}
            we honor legitimate requests promptly.
          </p>
        </section>

        {/* ── 7. Sharing ── */}
        <section id="sharing" className="terms-section">
          <h3>7. How We Share Information</h3>
          <p>We share information only in these limited situations:</p>
          <ul>
            <li>
              <strong>Publicly, at your direction:</strong> business listing
              content you choose to publish is visible to Platform visitors.
            </li>
            <li>
              <strong>With service providers:</strong> companies that help us
              operate the Platform (Section 8), only as needed to provide
              their services.
            </li>
            <li>
              <strong>For legal reasons:</strong> if required by law, legal
              process, or to protect the rights, safety, and security of
              GoOurHub, our users, or the public.
            </li>
            <li>
              <strong>Business transfers:</strong> if GoOurHub is involved in a
              merger, acquisition, or sale of assets, your information may be
              transferred as part of that transaction, subject to this policy.
            </li>
          </ul>
          <p>
            We do <strong>not</strong> sell personal information, and we do not
            share it with third parties for their own advertising.
          </p>
        </section>

        {/* ── 8. Service providers ── */}
        <section id="service-providers" className="terms-section">
          <h3>8. Service Providers We Use</h3>
          <ul>
            <li>
              <strong>Stripe</strong> payment processing for HubMart.
            </li>
            <li>
              <strong>Amazon Web Services (AWS)</strong> backend
              infrastructure, including account registration and email
              verification.
            </li>
            <li>
              <strong>Google Firebase</strong> website hosting.
            </li>
            <li>
              <strong>EmailJS</strong> delivery of emails sent through our
              contact and signup forms.
            </li>
          </ul>
          <p>
            Each provider processes information only to perform its service for
            us and is bound by its own privacy and security commitments.
          </p>
        </section>

        {/* ── 9. Retention ── */}
        <section id="retention" className="terms-section">
          <h3>9. Data Retention</h3>
          <p>
            We keep your account information for as long as your account is
            active. If you delete your account or ask us to remove your
            information, we delete it within a reasonable time, except where we
            need to keep limited records to comply with legal obligations,
            resolve disputes, or enforce our agreements.
          </p>
        </section>

        {/* ── 10. Security ── */}
        <section id="security" className="terms-section">
          <h3>10. Security</h3>
          <p>
            We use reasonable technical and organizational measures to protect
            your information, including encrypted (HTTPS) connections across
            the Platform. No method of transmission or storage is 100% secure,
            but we work to protect your information and use reputable
            infrastructure providers that maintain industry-standard security
            practices.
          </p>
        </section>

        {/* ── 11. Rights ── */}
        <section id="your-rights" className="terms-section">
          <h3>11. Your Choices &amp; Rights</h3>
          <p>You can, at any time:</p>
          <ul>
            <li>Request a copy of the personal information we hold about you.</li>
            <li>Ask us to correct information that is inaccurate or outdated.</li>
            <li>
              Ask us to delete your account and personal information
              (including your phone number on its own, if you prefer).
            </li>
            <li>
              Claim, update, or remove a business listing via our{" "}
              <Link to="/partner-signup#joinForm" className="terms-link">
                opt-out form
              </Link>
              .
            </li>
          </ul>
          <p>
            To exercise any of these rights, email us at{" "}
            <a href="mailto:contact@goourhub.com" className="terms-link">
              contact@goourhub.com
            </a>
            . We respond to all legitimate requests promptly. Depending on
            where you live, you may have additional rights under local law; we
            honor applicable rights regardless of your location.
          </p>
        </section>

        {/* ── 12. Children ── */}
        <section id="children" className="terms-section">
          <h3>12. Children's Privacy</h3>
          <p>
            The Platform is intended for users 18 years of age and older. We do
            not knowingly collect personal information from anyone under 18. If
            you believe a minor has provided us personal information, please
            contact us and we will delete it.
          </p>
        </section>

        {/* ── 13. Mobile app ── */}
        <section id="mobile-app" className="terms-section">
          <h3>13. Mobile App</h3>
          <p>
            The GoOurHub mobile app provides the same experience as our website
            and collects the same information described in this policy no
            more. The app does not access your contacts, photos, microphone, or
            precise location, and it does not use your data to track you across
            other companies' apps or websites.
          </p>
        </section>

        {/* ── 14. Changes ── */}
        <section id="changes" className="terms-section">
          <h3>14. Changes to This Policy</h3>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will revise the "Last updated" date above and, for material
            changes, provide reasonable notice on the Platform. Continued use
            of the Platform after changes take effect constitutes acceptance of
            the updated policy.
          </p>
        </section>

        {/* ── 15. Contact ── */}
        <section id="contact" className="terms-section">
          <h3>15. Contact Us</h3>
          <p>
            Questions about privacy or this policy? We're happy to help.
            <br />
            Support:{" "}
            <a href="mailto:contact@goourhub.com" className="terms-link">
              contact@goourhub.com
            </a>
          </p>
        </section>

        <p className="terms-footer-note">
          ★ GoOurHub &nbsp; Where You Belong ★
        </p>
      </div>

      <Footer />
    </>
  );
}

export default PrivacyPage;
